import { siteTexts } from "../../../../shared/i18n/siteTexts";

export interface SiteHero {
  title: string;
  subtitle: string;
  ctaLabel: string;
  ctaLink: string;
  imageUrl: string;
}

export interface SiteAbout {
  title: string;
  description: string;
  bullets: string[];
}

export interface SiteCard {
  title: string;
  description: string;
  imageUrl: string;
}

export interface SiteEvent extends SiteCard {
  id?: string;
  date: string;
  address: string;
  details: string;
  amountPerPlayerCents?: number | null;
  registrationType?: "individual" | "fixed_pair";
}

export interface SiteProduct extends SiteCard {
  price: string;
}

export interface SiteInstagramPost {
  imageUrl: string;
  link: string;
  caption?: string;
  publishedAt?: string;
}

export interface SiteContent {
  hero: SiteHero;
  about: SiteAbout;
  highlights: SiteCard[];
  events: SiteEvent[];
  products: SiteProduct[];
  instagram: SiteInstagramPost[];
}

function normalizeSwimwearGuidance(text: string): string {
  const input = String(text || "");
  if (!input) return "";
  return input.replace(
    /Use\s*(?:<strong>\s*)?biqu[ií]ni(?:\s*<\/strong>)?\s*[—-]\s*evite roupa [ií]ntima/gi,
    "Homens: <strong>sunga</strong> · Mulheres: <strong>biquíni</strong> — evite roupa íntima",
  );
}

const DEFAULT_EVENT: SiteEvent = {
  id: "",
  title: "",
  date: "",
  address: "",
  description: "",
  details: "",
  imageUrl: "",
  amountPerPlayerCents: null,
  registrationType: "individual",
};

export const defaultSiteContent: SiteContent = {
  hero: {
    title: "Todos no Padel",
    subtitle:
      "Conectamos a comunidade, criamos desafios e valorizamos os eventos que fazem o padel crescer.",
    ctaLabel: "Ver próximos eventos",
    ctaLink: "#/desafios",
    imageUrl: "",
  },
  about: {
    title: "Sobre o TNP",
    description: [
      "A TNP - Todos no Padel nasceu da paixão de Stephany e Raphael pelo esporte e pela vontade de levar essa energia para mais pessoas. Mais do que jogar, acreditamos no poder do padel de conectar, desafiar e transformar - dentro e fora das quadras.",
      "Criamos a TNP com o propósito de formar uma comunidade ativa, acolhedora e competitiva, onde jogadores de diferentes níveis possam evoluir, se desafiar e, principalmente, se divertir.",
      "Promovemos experiências únicas através dos nossos formatos de eventos, como o Challenge, o Super 8 e o Poço, pensados para incentivar a integração, o espírito esportivo e a evolução constante dos participantes. Estamos sempre em movimento, conhecendo novos clubes, participando de rotativos e fortalecendo conexões dentro do universo do padel.",
      "Nosso objetivo vai além dos eventos: queremos disseminar o padel, impulsionar o crescimento do esporte e ajudar cada jogador a atingir seu próximo nível.",
      "E, expandindo esse propósito, nasceu a TNP Store, com uma curadoria de acessórios de padel selecionados para quem busca qualidade, estilo e performance dentro de quadra.",
      "Acreditamos que o padel é para todos. E o nosso convite é simples: venha fazer parte dessa comunidade.",
    ].join("\n\n"),
    bullets: [
      siteTexts.home.aboutFallbackItems[0],
      siteTexts.home.aboutFallbackItems[1],
      siteTexts.home.aboutFallbackItems[2],
    ],
  },
  highlights: [
    {
      title: "Destaques da loja",
      description: "Novos grips, bolas oficiais e kits de treino.",
      imageUrl: "",
    },
  ],
  events: [
    {
      title: "Challenge 09:00",
      date: "19/04/2026",
      address: "H2 Padel",
      description: "Games até 5, categoria A, dupla fixa e limite de 6 duplas.",
      details:
        "Challenge das 09:00 com categoria A (4/5 masc e 4/5 fem), formato de dupla fixa e jogos contra todas as duplas.",
      imageUrl: "/events/desafio-tnp-19-04-challenge.png",
    },
    {
      title: "Super 8 - Categoria 6",
      date: "19/04/2026 - 11:30",
      address: "H2 Padel",
      description:
        "Games até 4, formato rotativo e limite de 8 pessoas.",
      details:
        "Super 8 das 11:30 para categoria 6, com formato rotativo e partidas curtas para alta rotação.",
      imageUrl: "/events/desafio-tnp-19-04-super8.png",
    },
    {
      title: "Super 8 - Iniciante",
      date: "19/04/2026 - 13:30",
      address: "H2 Padel",
      description:
        "Games até 4, categoria iniciante e limite de 8 pessoas.",
      details:
        "Super 8 das 13:30 para iniciantes, formato rotativo com foco em experiência competitiva para quem está evoluindo.",
      imageUrl: "/events/desafio-tnp-19-04-programacao.png",
    },
  ],
  products: [
    {
      title: "Kit Performance",
      price: "R$ 219,00",
      description: "Raquete + bolas oficiais + overgrip.",
      imageUrl: "",
    },
  ],
  instagram: [
    {
      imageUrl: "",
      link: "https://instagram.com/todosnopadel",
    },
  ],
};

function normalizeArray<T>(
  value: unknown,
  fallback: T[],
  normalizeItem: (item: unknown, index: number) => T
): T[] {
  if (!Array.isArray(value)) return fallback;
  return value.map(normalizeItem);
}

export function normalizeSiteContent(input?: Partial<SiteContent> | null): SiteContent {
  const safe = (input && typeof input === "object" ? input : {}) as Partial<SiteContent>;

  return {
    hero: {
      ...defaultSiteContent.hero,
      ...(safe.hero || {}),
    },
    about: {
      ...defaultSiteContent.about,
      ...(safe.about || {}),
      bullets: Array.isArray(safe.about?.bullets)
        ? safe.about?.bullets.filter(Boolean)
        : defaultSiteContent.about.bullets,
    },
    highlights: normalizeArray(
      safe.highlights,
      defaultSiteContent.highlights,
      (item) => ({
        ...defaultSiteContent.highlights[0],
        ...(item as Partial<SiteCard>),
      })
    ),
    events: normalizeArray(safe.events, defaultSiteContent.events, (item, index) => {
      const parsed = (item && typeof item === "object" ? item : {}) as Record<string, unknown>;
      const id = String(parsed?.id || "").trim() || `evt-${index}`;
      return {
        ...DEFAULT_EVENT,
        title: String(parsed?.title || DEFAULT_EVENT.title),
        date: String(parsed?.date || DEFAULT_EVENT.date),
        address: String(parsed?.address || DEFAULT_EVENT.address),
        description: normalizeSwimwearGuidance(
          String(parsed?.description || DEFAULT_EVENT.description),
        ),
        details: normalizeSwimwearGuidance(
          String(parsed?.details || DEFAULT_EVENT.details),
        ),
        imageUrl: String(parsed?.imageUrl || DEFAULT_EVENT.imageUrl),
        amountPerPlayerCents: Number.isFinite(Number(parsed?.amountPerPlayerCents))
          ? Math.max(0, Math.round(Number(parsed?.amountPerPlayerCents)))
          : null,
        registrationType:
          String(parsed?.registrationType || "").trim().toLowerCase() === "fixed_pair"
            ? "fixed_pair"
            : "individual",
        id,
      };
    }),
    products: normalizeArray(
      safe.products,
      defaultSiteContent.products,
      (item) => ({
        ...defaultSiteContent.products[0],
        ...(item as Partial<SiteProduct>),
      })
    ),
    instagram: normalizeArray(
      safe.instagram,
      defaultSiteContent.instagram,
      (item) => ({
        ...defaultSiteContent.instagram[0],
        ...(item as Partial<SiteInstagramPost>),
      })
    ),
  };
}
